import { Component, EventEmitter, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TicketClientService } from "@app2/clients/ticket-client.service";
import {
    incidentStateOptions,
    SlugName,
    StateOptions,
    TASK_SLUG,
    taskStateOptions,
    TicketState,
} from "@app2/ticket/ticket-constants";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { defaultTicketStatesForSlug, makePageFilters } from "@app2/ticket/ticket-list-page/utils";
import { TimeService } from "@app2/shared/services/time.service";
import { Ticket, TicketPageRequest, TicketSummary } from "@app2/type-defs/ticket/ticket-types";
import * as _ from "underscore";
import { getMessageForError } from "@app2/util/errors/handleable-errors";

interface DialogData {
    titleKey: string;
    slugName: SlugName;
    ticket: Ticket; // Reference ticket
    canSelectMultipleTickets: boolean;
    excludeReferenceTicket: boolean;
    returnAndClose: boolean; // Whether to return the selected ticket and close the modal immediately
    showOwner: boolean;
}

@Component({
    selector: "ticket-selection-modal",
    template: `
        <span mat-dialog-title>{{ data.titleKey | localize }}</span>
        <mat-dialog-content>
            <div class="sheet"
                 *dsLoading="loading">
                <header class="content-header">
                    <ticket-state-dropdown [slugName]="data.slugName"
                                           [selectedStates]="ticketStates"
                                           (statesChanged)="stateChanged($event)"
                                           test-class="state-filter">
                    </ticket-state-dropdown>
                    <form (submit)="refreshPages()" class="search-filter">
                        <input [(ngModel)]="searchString"
                               name="searchString"
                               type="text"
                               [placeholder]="'TICKET_SEARCH' | localize"/>
                        <button type="submit">
                            <i class="ion-ios-search-strong"></i>
                        </button>
                    </form>
                </header>
                <ticket-embedded-list [showHeaders]="true"
                                      [tickets]="tickets"
                                      [canSelectTickets]="true"
                                      [canSelectMultipleTickets]="data.canSelectMultipleTickets"
                                      [canNavigateToTickets]="false"
                                      [showOwner]="data.showOwner"
                                      [showDueDate]="true"
                                      [showCreatedBy]="true"
                                      [showUpdatedBy]="true"
                                      (onSelected)="selectedTickets = $event">
                </ticket-embedded-list>
            </div>
            <pagination-detail *ngIf="!loading"
                               [totalCount]="totalCount"
                               [pageNumber]="pageNumber"
                               [pageSize]="10"
                               (onSelectPage)="setPageNumber($event)">
            </pagination-detail>
        </mat-dialog-content>
        <mat-dialog-actions>
            <modal-buttons [canSave]="canCommit()"
                           (onCancel)="onCancel()"
                           (onSave)="onSave()">
            </modal-buttons>
        </mat-dialog-actions>
    `,
})
export class TicketSelectionModalComponent implements OnInit {

    tickets: Ticket[];
    onSelected = new EventEmitter<Ticket>();
    onSelectedMultiple = new EventEmitter<Ticket[]>();
    stateOptions: StateOptions;
    selectedTickets: Ticket[];
    loading: boolean;
    searchString: string = "";
    ticketStates: TicketState[];
    totalCount: number;
    pages: Record<number, TicketSummary>;
    pageNumber: number;

    constructor(private ticketService: TicketClientService,
                private notificationsService: NotificationsService,
                private timeService: TimeService,
                private dialogRef: MatDialogRef<TicketSelectionModalComponent>,
                @Inject(MAT_DIALOG_DATA) public data: DialogData) {
        this.stateOptions = data.slugName === TASK_SLUG ? taskStateOptions : incidentStateOptions;
        this.ticketStates = defaultTicketStatesForSlug(data.slugName);
    }

    ngOnInit() {
        this.refreshPages();
    }

    setPageNumber(page: number) {
        this.pageNumber = page;
        this.refreshPage();
    }

    stateChanged(options: Partial<Record<string, boolean>>) {
        this.ticketStates = <TicketState[]>Object.keys(options).filter(key => options[key]);
        this.refreshPages();
    }

    canCommit(): boolean {
        return this.data.canSelectMultipleTickets ?
            _.keys(this.selectedTickets).length > 0 : _.keys(this.selectedTickets).length === 1;
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        let selected;
        if (this.data.canSelectMultipleTickets) {
            selected = this.selectedTickets;
            this.onSelectedMultiple.emit(selected);
        } else {
            selected = this.selectedTickets[0];
            this.onSelected.emit(selected);
        }

        if (this.data.returnAndClose) {
            this.dialogRef.close(selected);
            return;
        }
    }

    refreshPages() {
        this.loading = true;

        this.ticketService.getTicketPages(this.data.slugName, this.getPageRequest())
            .then(tickets => {
                this.totalCount = tickets.totalCount;
                this.pages = tickets.ticketSummaries;
                this.pageNumber = 1;
                return this.refreshPage(false);
            })
            .catch(error => {
                const message = getMessageForError(error.error, "ERROR_LOADING_TICKETS");
                this.notificationsService.showError(message, error);
            })
            .then(() => this.loading = false);
    }

    private refreshPage(updateLoading = true) {
        if (updateLoading) {
            this.loading = true;
        }
        return this.ticketService.getTicketPage(this.data.slugName, this.getPageRequest(this.pages[this.pageNumber - 1]))
            .then(tickets => this.tickets = _.filter(
                tickets, t => t.slugId !== this.data.ticket?.slugId || !this.data.excludeReferenceTicket))
            .catch(error => {
                const message = getMessageForError(error.error, "ERROR_LOADING_TICKETS");
                this.notificationsService.showError(message, error);
            })
            .then(() => {
                if (updateLoading) {
                    this.loading = false;
                }
            });
    }

    private getPageRequest(page?: TicketSummary): Partial<TicketPageRequest> {
        return {
            pageSize: 10,
            pageStart: page,
            sort: "UPDATED_ON",
            descending: true,
            filters: makePageFilters(this.timeService, {
                query: this.searchString,
                ticketState: this.ticketStates,
            }),
            numLogs: 0,
        };
    }
}
